/**
 * Simple in-memory cache with TTL (Time To Live) support
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // TTL in milliseconds
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();

  /**
   * Set a cache entry with TTL
   */
  set<T>(key: string, data: T, ttlMs: number = 120000): void { // Default 2 minutes
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
  }

  /**
   * Get a cache entry if it exists and is not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if entry has expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Check if a cache entry exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear a specific cache entry
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Create a singleton instance for projects cache
export const projectsCache = new SimpleCache();

// Utility function to generate cache keys for projects
export const generateProjectsCacheKey = (
  userId: string,
  statusFilter: string,
  projectTypeFilter: string
): string => {
  return `projects_${userId}_${statusFilter}_${projectTypeFilter}`;
};

// Auto-cleanup expired entries every 5 minutes
setInterval(() => {
  projectsCache.clearExpired();
}, 5 * 60 * 1000);